import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCaretDown, faFilter } from '@fortawesome/free-solid-svg-icons';
import { Observable, Subject, map, startWith, tap } from 'rxjs';
import { Organization } from 'src/app/models/organization';

@Component({
    selector: 'app-organization-selector',
    templateUrl: './organization-selector.component.html',
    styleUrls: ['./organization-selector.component.scss'],
    imports: [FormsModule, FaIconComponent, MatInputModule, MatAutocompleteModule, ReactiveFormsModule, MatOptionModule, AsyncPipe]
})
export class OrganizationSelectorComponent implements OnInit, OnDestroy {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faFilter = faFilter;
    public faCaretDown = faCaretDown;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Valeur indiquant si le controle de selection de l'organisation est visible.
     */
    @Input()
    visible: boolean;

    /**
     * Liste des organisations selectionnables.
     */
    private _organizations: Organization[];

    /**
     * Obtient la liste des organisations selectionnables.
     */
    @Input()
    set organizations(value: Organization[]) {
        this._organizations = value;
    }

    /**
     * Définie la liste des organisations selectionnables.
     */
    get organizations(): Organization[] {
        return this._organizations;
    }

    /**
     * Organisation selectionnée par défaut.
     */
    private _defaultSelectedOrganization: Organization;

    /**
     * Obtient l'organisation selectionnée par défaut.
     */
    @Input()
    set defaultSelectedOrganization(value: Organization) {
        if (this.selectFormControl != null && this._defaultSelectedOrganization?.id !== value?.id) {
            this.selectFormControl.setValue(value);
        }

        this._defaultSelectedOrganization = value;
    }

    /**
     * Définie l'organisation selectionnée par défaut.
     */
    get defaultSelectedOrganization(): Organization {
        return this._defaultSelectedOrganization;
    }

    /**
     * Emis lorsque l'utilisateur selectionne une organisation.
     */
    @Output()
    public organizationSelected: EventEmitter<Organization> = new EventEmitter();

    /**
     * Organisation selectionnée par l'utilisateur.
     */
    selectedOrganization: Organization;

    /**
     * Organisations affichées dans la liste déroulante.
     */
    filteredOrganizations: Observable<Organization[]>;

    /**
     * Contrôle utilisé pour la saisie de l'organisation et l'affichage de l'organisation selectionnée.
     */
    selectFormControl: UntypedFormControl;

    // @ViewChild(MatAutocompleteTrigger) trigger: MatAutocompleteTrigger;

    /**
     * Constructeur.
     */
    constructor() {}

    /**
     * Initialise le composant.
     */
    ngOnInit() {
        // Initialise le Contrôle utilisé pour la saisie de l'organisation avec l'organisation par défaut.
        this.selectFormControl = new UntypedFormControl(this.defaultSelectedOrganization);

        // S'abonne aux événements de saisie de l'utilisateur.
        this.filteredOrganizations = this.selectFormControl.valueChanges.pipe(
            startWith(''),
            tap((value: string | Organization) => console.log('Change value : ', value)),
            map((value: string | Organization) => {
                if (typeof value === 'string') {
                    return this.filter((value as string) || '');
                } else {
                    if (value != null) {
                        this._defaultSelectedOrganization = value as Organization;
                        this.organizationSelected.emit(value as Organization);
                    } else {
                        this.organizationSelected.emit(this._defaultSelectedOrganization);
                    }

                    return this.organizations;
                }
            })
        );
    }

    /**
     * Appellé par l'autocomplete pour déterminer le libellé à afficher pour une organization.
     * @param organization : L'organisation à afficher
     * @returns : Le libellé de l'organisation.
     */
    displayLabel(organization: Organization) {
        return organization ? organization.name : '';
    }

    /**
     * Détruit le composant.
     */
    ngOnDestroy() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Appelée lorsque la liste déroulante se ferme.
     */
    public closed(): void {
        // Si le texte saisie ne correspond pas à une organisation mais à du texte saisie par l'utilisateur.
        if (typeof this.selectFormControl.value === 'string') {
            // Réinitialise le champ avec l'organisation par défaut.
            this.selectFormControl.setValue(this.defaultSelectedOrganization);
        }
    }

    /**
     * Lors de la perte de focus de l'input
     */
    public onFocusOut(): void {
        // Si le texte saisie ne correspond pas à une organisation mais à du texte saisie par l'utilisateur.
        if (typeof this.selectFormControl.value === 'string') {
            // Réinitialise le champ avec l'organisation par défaut.
            //this.selectFormControl.setValue(this.defaultSelectedOrganization);
        }
    }

    /**
     * Appelée lorsque la liste déroulante s'ouvre.
     */
    public opened(): void {
        // Réinitialise le champ à vide.
        this.selectFormControl.setValue('');
    }

    /**
     * Filtre les organisations correspondant au texte saisie par l'utilisateur.
     * @param value : texte saisie par l'utilisateur.
     * @returns Les organisations filtrées.
     */
    private filter(value: string): Organization[] {
        const filterValue = value.toLowerCase();
        return this.organizations.filter((organization) => organization.name.toLowerCase().includes(filterValue));
    }
}
